// @ts-nocheck
import { useState } from 'react';
import {
  Stack,
  Title,
  Text as MantineText,
  Code,
  Divider,
  NumberInput,
  MultiSelect,
  Radio,
  Textarea,
  Group,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconUser, IconMail, IconSearch } from '@tabler/icons-react';
import { Card, TextInput, Select, Checkbox, Switch } from '../../../components/ui';
import { mockCategories, mockCountries } from '../mockData';

export default function InputsPage() {
  const [checked, setChecked] = useState(false);
  const [switchValue, setSwitchValue] = useState(false);
  const [radioValue, setRadioValue] = useState('react');

  return (
    <Stack gap="xl">
      {/* Page Header */}
      <Stack gap="xs">
        <Title order={2}>Input Components</Title>
        <MantineText c="dimmed" size="sm">
          Form inputs and controls for collecting user data
        </MantineText>
      </Stack>

      {/* TextInput */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>TextInput</Title>
            <MantineText size="sm" c="dimmed">
              Standard text input field with label, description, and error support
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Examples:</MantineText>
            <Stack gap="sm">
              <TextInput label="Your name" placeholder="Enter your name" />
              <TextInput
                label="Email"
                placeholder="your@email.com"
                leftSection={<IconMail size={16} />}
              />
              <TextInput
                label="Search"
                placeholder="Search..."
                leftSection={<IconSearch size={16} />}
                description="Search for anything"
              />
              <TextInput
                label="Username"
                placeholder="Username"
                error="Username is already taken"
                leftSection={<IconUser size={16} />}
              />
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<TextInput
  label="Your name"
  placeholder="Enter your name"
/>

<TextInput
  label="Email"
  placeholder="your@email.com"
  leftSection={<IconMail size={16} />}
/>

<TextInput
  label="Username"
  error="Username is already taken"
  leftSection={<IconUser size={16} />}
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Select & MultiSelect */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Select & MultiSelect</Title>
            <MantineText size="sm" c="dimmed">
              Dropdown selection components for single or multiple choices
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Select (Single Choice):</MantineText>
            <Select
              label="Country"
              placeholder="Select country"
              data={mockCountries}
              searchable
            />
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Select
  label="Country"
  placeholder="Select country"
  data={countries}
  searchable
/>`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>MultiSelect (Multiple Choices):</MantineText>
            <MultiSelect
              label="Categories"
              placeholder="Select categories"
              data={mockCategories}
              searchable
            />
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<MultiSelect
  label="Categories"
  placeholder="Select categories"
  data={categories}
  searchable
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Checkbox, Radio, Switch */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Checkbox, Radio & Switch</Title>
            <MantineText size="sm" c="dimmed">
              Boolean and selection inputs for toggles and choices
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Checkbox:</MantineText>
            <Stack gap="xs">
              <Checkbox
                label="I agree to terms and conditions"
                checked={checked}
                onChange={(event) => setChecked(event.currentTarget.checked)}
              />
              <Checkbox label="Subscribe to newsletter" />
              <Checkbox label="Enable notifications" defaultChecked />
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Checkbox
  label="I agree to terms and conditions"
  checked={checked}
  onChange={(event) => setChecked(event.currentTarget.checked)}
/>
<Checkbox label="Subscribe to newsletter" />
<Checkbox label="Enable notifications" defaultChecked />`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Radio Group:</MantineText>
            <Radio.Group
              label="Select your favorite framework"
              value={radioValue}
              onChange={setRadioValue}
            >
              <Group mt="xs">
                <Radio value="react" label="React" />
                <Radio value="vue" label="Vue" />
                <Radio value="angular" label="Angular" />
                <Radio value="svelte" label="Svelte" />
              </Group>
            </Radio.Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Radio.Group
  label="Select your favorite framework"
  value={value}
  onChange={setValue}
>
  <Group mt="xs">
    <Radio value="react" label="React" />
    <Radio value="vue" label="Vue" />
    <Radio value="angular" label="Angular" />
  </Group>
</Radio.Group>`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Switch:</MantineText>
            <Stack gap="xs">
              <Switch
                label="Enable dark mode"
                checked={switchValue}
                onChange={(event) => setSwitchValue(event.currentTarget.checked)}
              />
              <Switch label="Enable notifications" />
              <Switch label="Auto-save changes" defaultChecked />
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Switch
  label="Enable dark mode"
  checked={value}
  onChange={(event) => setValue(event.currentTarget.checked)}
/>
<Switch label="Enable notifications" />
<Switch label="Auto-save changes" defaultChecked />`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Textarea & Other Inputs */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Textarea & Other Inputs</Title>
            <MantineText size="sm" c="dimmed">
              Additional input types for various data formats
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Textarea:</MantineText>
            <Textarea
              label="Description"
              placeholder="Enter description"
              description="Provide a detailed description"
              minRows={3}
            />
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Textarea
  label="Description"
  placeholder="Enter description"
  description="Provide a detailed description"
  minRows={3}
/>`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>NumberInput:</MantineText>
            <NumberInput
              label="Age"
              placeholder="Enter your age"
              min={0}
              max={120}
            />
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<NumberInput
  label="Age"
  placeholder="Enter your age"
  min={0}
  max={120}
/>`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>DateInput:</MantineText>
            <DateInput
              label="Birth date"
              placeholder="Pick date"
            />
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<DateInput
  label="Birth date"
  placeholder="Pick date"
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
